import { ReferenceText } from '@scale/shared/types/chat';
import { useState } from 'react';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import EditReferenceTextModal from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/EditReferenceTextModal';

type ReferenceTextWithCategoryChipProps = {
  referenceText: ReferenceText;
  setReferenceText: (referenceText: ReferenceText) => void;
  index: number;
  deleteReferenceText: () => void;
  readOnly?: boolean;
  required?: boolean;
  showReferenceCategory?: boolean;
  referenceTextCategories?: string;
  minReferenceTextsLength?: number;
  maxReferenceTextsLength?: number;
};

const ReferenceTextWithCategoryChip = ({
  referenceText,
  setReferenceText,
  deleteReferenceText,
  index,
  showReferenceCategory = false,
  readOnly,
  referenceTextCategories,
  minReferenceTextsLength,
  maxReferenceTextsLength,
}: ReferenceTextWithCategoryChipProps) => {
  const [isEditingOther, setIsEditingOther] = useState(false);
  const [otherCategory, setOtherCategory] = useState('');
  const [showCategorySelect, setShowCategorySelect] = useState(
    readOnly ? false : referenceText.category ? false : showReferenceCategory,
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  const categories = (referenceTextCategories?.split(',') || [])
    .map(category => category.trim())
    .concat(['Other']);

  const handleCategorySelect = (selectedCategory: string) => {
    if (selectedCategory === 'Other') {
      setIsEditingOther(true);
    } else {
      setReferenceText({
        ...referenceText,
        category: selectedCategory,
      });
      setShowCategorySelect(false);
    }
  };

  const handleOtherSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setReferenceText({
      ...referenceText,
      category: otherCategory,
    });
    setIsEditingOther(false);
    setShowCategorySelect(false);
  };

  const CustomChip = () => {
    return (
      <div className="scaleui inline-flex items-center rounded bg-neutral-100 py-0.5 text-xs font-medium text-neutral-700 transition-colors w-fit px-2">
        <FontAwesomeIcon icon={faLink} className="mr-1 text-neutral-400" />
        <span className="mr-1 text-neutral-400">#{index + 1}</span>

        <span
          onClick={() => {
            setShowModal(true);
          }}
          className="cursor-pointer"
        >
          {referenceText.name}
        </span>

        {showReferenceCategory &&
          (isEditingOther ? (
            <form onSubmit={handleOtherSubmit} className="inline-flex items-center ml-1">
              <input
                type="text"
                value={otherCategory}
                onChange={e => setOtherCategory(e.target.value)}
                className="text-xs bg-white border rounded px-1 py-0.5 w-24"
                placeholder="Category"
                autoFocus
              />
              <button type="submit" className="text-primary-900 text-xs px-1">
                Save
              </button>
            </form>
          ) : showCategorySelect ? (
            <select
              value={referenceText.category || ''}
              required
              onChange={e => handleCategorySelect(e.target.value)}
              className="text-xs border rounded pl-2 pr-6 py-0.5 bg-white w-full ml-1"
              disabled={readOnly}
            >
              <option value="" disabled>
                Please select a category
              </option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          ) : (
            referenceText.category && (
              <span
                className={classnames(
                  'ml-1 text-xs bg-primary-100 text-primary-900 px-2 py-0.5 rounded',
                  {
                    'cursor-pointer': !readOnly,
                    'cursor-not-allowed opacity-50': readOnly,
                  },
                )}
                onClick={() => !readOnly && setShowCategorySelect(true)}
              >
                {referenceText.category}
              </span>
            )
          ))}

        {!readOnly && (
          <button
            onClick={deleteReferenceText}
            className="ml-0.5 -mr-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-200 hover:text-neutral-500 focus:bg-neutral-500 focus:text-neutral-0 focus:outline-none"
          >
            <svg className="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
              <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <CustomChip />
      {/* Since the reference text is uploaded via file upload, the reference text modal is set to read-only. */}
      <EditReferenceTextModal
        label={referenceText.name}
        isOpen={showModal}
        required={false}
        hideUrl={true}
        readOnly={true}
        showReferenceCategory={showReferenceCategory}
        onClose={() => {
          setShowModal(false);
        }}
        referenceText={referenceText}
        setReferenceText={setReferenceText}
        setIsOpen={setShowModal}
        minReferenceTextsLength={minReferenceTextsLength}
        maxReferenceTextsLength={maxReferenceTextsLength}
      />
    </>
  );
};

export default ReferenceTextWithCategoryChip;
